import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  InputField,
  Select,
  ToggleSwitch,
  Tooltip,
  type SelectOption,
} from '@enamelai/design-system'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { logger } from '@utils/safeLogger'
import {
  getSchedulerSettings,
  applySchedulerSettings,
} from '@services/api/appointment.api'
import { useAppSelector } from '@store/hooks'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import {
  BUFFER_OPTIONS,
  CALENDAR_INTERVAL_OPTIONS,
  DEFAULT_CALENDAR_VIEW_OPTIONS,
  LATE_CANCELLATION_OPTIONS,
  NO_SHOW_ACTION_OPTIONS,
  RESCHEDULE_WINDOW_OPTIONS,
  SCHEDULER_TOGGLES,
  SCHEDULER_VIEW_MODE_ENABLED,
  START_HOUR_OPTIONS,
  TIME_SLOT_INTERVAL_OPTIONS,
  VIEW_MODE_OPTIONS,
  YES_NO_OPTIONS,
  getEndHourOptions,
  getHourCountOptions,
} from './scheduler.constants'
import SchedulerPreview from './SchedulerPreview.component'
import './SchedulerStyle.css'

export type SchedulerSettingsType = {
  view_mode: 'Fixed' | 'Full'
  working_hours_from: number
  working_hours_to: number
  time_slot_interval: number
  pre_appointment_buffer: number
  post_appointment_buffer: number
  calendar_intervals: number
  assign_random_provider: boolean
  default_calendar_view: 'Day' | 'Week' | 'Month'
  show_appointment_type: boolean
  show_appointment_duration: boolean
  overlap_appointments: boolean
  show_provider_information: boolean
  status_indicator: boolean
  allow_double_booking: boolean
  schedule_recall_automatically: boolean
  require_patient_confirmation: boolean
  show_cancelled_appointments: boolean
  send_reminder_before: string
  auto_confirm_after: string
  reschedule_window: string
  no_show_action: string
  no_show_fee: string
  late_cancellation_window: string
}

export const INITIAL_SCHEDULER_SETTINGS_DATA: SchedulerSettingsType = {
  view_mode: 'Fixed',
  working_hours_from: 8,
  working_hours_to: 17,
  time_slot_interval: 15,
  pre_appointment_buffer: 15,
  post_appointment_buffer: 30,
  calendar_intervals: 15,
  assign_random_provider: false,
  default_calendar_view: 'Day',
  show_appointment_type: true,
  show_appointment_duration: true,
  overlap_appointments: true,
  show_provider_information: true,
  status_indicator: true,
  allow_double_booking: true,
  schedule_recall_automatically: true,
  require_patient_confirmation: true,
  show_cancelled_appointments: false,
  send_reminder_before: '48',
  auto_confirm_after: '24',
  reschedule_window: '12 hours before appointment',
  no_show_action: 'Flag on patient record',
  no_show_fee: '',
  late_cancellation_window: 'Less than 4 hours',
}

type NumericSettingKey =
  | 'working_hours_from'
  | 'working_hours_to'
  | 'time_slot_interval'
  | 'pre_appointment_buffer'
  | 'post_appointment_buffer'
  | 'calendar_intervals'

const NUMERIC_KEYS: NumericSettingKey[] = [
  'working_hours_from',
  'working_hours_to',
  'time_slot_interval',
  'pre_appointment_buffer',
  'post_appointment_buffer',
  'calendar_intervals',
]

/** Older saves stored select values as strings ("15", "Yes"). */
const normalizeSettings = (
  raw: Partial<Record<keyof SchedulerSettingsType, unknown>> | null,
): SchedulerSettingsType => {
  const merged = {
    ...INITIAL_SCHEDULER_SETTINGS_DATA,
    ...(raw ?? {}),
  } as SchedulerSettingsType
  NUMERIC_KEYS.forEach((key) => {
    const value = Number(merged[key])
    merged[key] = Number.isFinite(value)
      ? value
      : INITIAL_SCHEDULER_SETTINGS_DATA[key]
  })
  const assign = raw?.assign_random_provider
  merged.assign_random_provider =
    assign === true || assign === 'true' || assign === 'Yes'
  merged.no_show_fee = merged.no_show_fee ? String(merged.no_show_fee) : ''
  return merged
}

type InfoLabelProps = {
  label: string
  info: string
}

const InfoLabel: React.FC<InfoLabelProps> = ({ label, info }) => (
  <span className="scheduler-field-label">
    {label}
    <Tooltip content={info}>
      <span className="scheduler-info-icon" aria-label={info}>
        <InfoIcon aria-hidden="true" />
      </span>
    </Tooltip>
  </span>
)

const Scheduler: React.FC = () => {
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const { setHeaderButtons } = useSettingsHeader()
  const [formData, setFormData] = useState<SchedulerSettingsType>(
    INITIAL_SCHEDULER_SETTINGS_DATA,
  )
  const [savedFormData, setSavedFormData] = useState<SchedulerSettingsType>(
    INITIAL_SCHEDULER_SETTINGS_DATA,
  )
  const [dataReady, setDataReady] = useState(false)
  const [saving, setSaving] = useState(false)
  useSettingsSubpageLoading(!dataReady)

  const hasChanges = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(savedFormData),
    [formData, savedFormData],
  )

  const fetchData = useCallback(async (): Promise<void> => {
    setDataReady(false)
    try {
      const response = await getSchedulerSettings()
      if (
        response.statusCode === 200 &&
        'scheduler_settings' in response.data
      ) {
        const settings = normalizeSettings(response.data.scheduler_settings)
        setFormData(settings)
        setSavedFormData(settings)
      }
    } catch (error) {
      logger.error('Failed to load scheduler settings', error)
    } finally {
      setDataReady(true)
    }
  }, [])

  useEffect(() => {
    void fetchData()
  }, [fetchData, selectedClinic])

  const setField = useCallback(
    <K extends keyof SchedulerSettingsType>(
      name: K,
      value: SchedulerSettingsType[K],
    ): void => {
      setFormData((prev) => ({ ...prev, [name]: value }))
    },
    [],
  )

  const setStartHour = useCallback((hour: number): void => {
    setFormData((prev) => ({
      ...prev,
      working_hours_from: hour,
      working_hours_to: Math.max(prev.working_hours_to, hour + 1),
    }))
  }, [])

  const handleCancel = useCallback((): void => {
    setFormData(savedFormData)
  }, [savedFormData])

  const handleSave = useCallback(async (): Promise<void> => {
    setSaving(true)
    showLoading({ container: 'scheduler-settings-page' })
    const failedToast = createFailedToast(
      'Scheduler settings applying failed',
      'Scheduler settings applying failed. Please try again later.',
    )
    try {
      const { no_show_fee, ...rest } = formData
      const response = await applySchedulerSettings({
        ...rest,
        ...(no_show_fee ? { no_show_fee: no_show_fee.replace(/,/g, '') } : {}),
      })
      if (response.statusCode === 200) {
        setSavedFormData(formData)
        localStorage.setItem(
          'default_calendar_view',
          formData.default_calendar_view.toLowerCase(),
        )
        showToast(
          createSuccessToast(
            'Scheduler settings applied successfully',
            'Scheduler settings have been applied successfully.',
          ),
        )
      } else {
        showToast(failedToast)
      }
    } catch (error) {
      logger.error('Failed to apply scheduler settings', error)
      showToast(failedToast)
    } finally {
      hideLoading('scheduler-settings-page')
      setSaving(false)
    }
  }, [formData, hideLoading, showLoading, showToast])

  const actionsDisabled = !dataReady || saving || !hasChanges

  useEffect(() => {
    setHeaderButtons(
      <>
        <Button
          className="settings-ds-button"
          variant="secondary"
          size="md"
          type="button"
          disabled={actionsDisabled}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          className="settings-ds-button"
          variant="primary"
          size="md"
          type="button"
          disabled={actionsDisabled}
          onClick={() => {
            void handleSave()
          }}
        >
          Save changes
        </Button>
      </>,
    )
  }, [actionsDisabled, handleCancel, handleSave, setHeaderButtons])

  useEffect(() => () => setHeaderButtons(null), [setHeaderButtons])

  const numberSelect = (
    key: NumericSettingKey,
    label: string,
    options: SelectOption[],
  ): React.ReactNode => (
    <Select
      label={label}
      placeholder="Select"
      options={options}
      value={String(formData[key])}
      disabled={!dataReady}
      onChange={(value) => setField(key, Number(value))}
    />
  )

  const textSelect = (
    key:
      | 'send_reminder_before'
      | 'auto_confirm_after'
      | 'reschedule_window'
      | 'no_show_action'
      | 'late_cancellation_window',
    label: string,
    options: SelectOption[],
  ): React.ReactNode => (
    <Select
      label={label}
      placeholder="Select"
      options={options}
      value={formData[key]}
      disabled={!dataReady}
      onChange={(value) => setField(key, String(value))}
    />
  )

  const bufferField = (
    key: 'pre_appointment_buffer' | 'post_appointment_buffer',
    label: string,
    info: string,
  ): React.ReactNode => (
    <div className="scheduler-field">
      <InfoLabel label={label} info={info} />
      {/* The label is rendered by InfoLabel above; an empty label stops the
          DS Select from falling back to its demo label ("Preferred time"). */}
      <Select
        label=""
        placeholder="Select"
        options={BUFFER_OPTIONS}
        value={String(formData[key])}
        disabled={!dataReady}
        onChange={(value) => setField(key, Number(value))}
      />
    </div>
  )

  return (
    <div className="scheduler-settings" id="scheduler-settings-page">
      <section className="scheduler-section">
        <h2 className="scheduler-section-title">Scheduler settings</h2>
        <SchedulerPreview settings={formData} />

        <div
          className={`scheduler-fields ${
            SCHEDULER_VIEW_MODE_ENABLED
              ? 'scheduler-fields--thirds'
              : 'scheduler-fields--halves'
          }`}
        >
          <Select
            label="Scheduler start time"
            leadingIcon="clock"
            options={START_HOUR_OPTIONS}
            value={String(formData.working_hours_from)}
            disabled={!dataReady}
            onChange={(value) => setStartHour(Number(value))}
          />
          <Select
            label="Scheduler end time"
            leadingIcon="clock"
            options={getEndHourOptions(formData.working_hours_from)}
            value={String(formData.working_hours_to)}
            disabled={!dataReady}
            onChange={(value) => setField('working_hours_to', Number(value))}
          />
          {SCHEDULER_VIEW_MODE_ENABLED ? (
            <Select
              label="View mode"
              options={VIEW_MODE_OPTIONS}
              value={formData.view_mode}
              disabled={!dataReady}
              onChange={(value) =>
                setField(
                  'view_mode',
                  value as SchedulerSettingsType['view_mode'],
                )
              }
            />
          ) : null}
        </div>

        <div className="scheduler-fields scheduler-fields--thirds">
          {numberSelect(
            'time_slot_interval',
            'Time slot interval',
            TIME_SLOT_INTERVAL_OPTIONS,
          )}
          {bufferField(
            'pre_appointment_buffer',
            'Pre-appointment buffer',
            'Time blocked before each appointment for preparation.',
          )}
          {bufferField(
            'post_appointment_buffer',
            'Post-appointment buffer',
            'Time blocked after each appointment for cleaning and turnover.',
          )}
        </div>

        <div className="scheduler-fields scheduler-fields--thirds">
          {numberSelect(
            'calendar_intervals',
            'Scheduler Intervals',
            CALENDAR_INTERVAL_OPTIONS,
          )}
          <Select
            label="Assign random provider per appt."
            options={YES_NO_OPTIONS}
            value={formData.assign_random_provider ? 'yes' : 'no'}
            disabled={!dataReady}
            onChange={(value) =>
              setField('assign_random_provider', value === 'yes')
            }
          />
          <Select
            label="Default calendar view"
            options={DEFAULT_CALENDAR_VIEW_OPTIONS}
            value={formData.default_calendar_view}
            disabled={!dataReady}
            onChange={(value) =>
              setField(
                'default_calendar_view',
                value as SchedulerSettingsType['default_calendar_view'],
              )
            }
          />
        </div>

        <div className="scheduler-toggles">
          {SCHEDULER_TOGGLES.map(({ key, label }) => (
            <label key={key} className="scheduler-toggle">
              <ToggleSwitch
                checked={formData[key]}
                disabled={!dataReady}
                aria-label={label}
                onChange={(checked) => setField(key, checked)}
              />
              <span className="scheduler-toggle-label">{label}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="scheduler-section">
        <h2 className="scheduler-section-title">
          Reminders &amp; cancellation
        </h2>
        <div className="scheduler-fields scheduler-fields--thirds">
          {textSelect(
            'send_reminder_before',
            'Send appt. reminder before',
            getHourCountOptions(formData.send_reminder_before),
          )}
          {textSelect(
            'auto_confirm_after',
            'Auto-confirm after',
            getHourCountOptions(formData.auto_confirm_after),
          )}
          {textSelect(
            'reschedule_window',
            'Reschedule window',
            RESCHEDULE_WINDOW_OPTIONS,
          )}
        </div>
        <div className="scheduler-fields scheduler-fields--thirds">
          {textSelect(
            'no_show_action',
            'If patient did not show',
            NO_SHOW_ACTION_OPTIONS,
          )}
          <InputField
            label="No-show fee"
            variant="amount"
            placeholder="50"
            fullWidth
            value={formData.no_show_fee}
            disabled={!dataReady}
            onChange={(event) => setField('no_show_fee', event.target.value)}
            onClear={() => setField('no_show_fee', '')}
          />
          {textSelect(
            'late_cancellation_window',
            'Late cancellation window',
            LATE_CANCELLATION_OPTIONS,
          )}
        </div>
      </section>
    </div>
  )
}

export default Scheduler
